import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { SuperPageServices, type GetSuperPageAssignmentsParams } from "../services/bedrock/SuperPageServices";
import { CancelRequestOptions } from "@/services/bedrock";

function buildSuperPageAssignmentsQueryOptions(
  userId: number | undefined,
  params: GetSuperPageAssignmentsParams = {},
  options?: CancelRequestOptions
) {
  const extraFieldsKey = params.extraFields ? JSON.stringify(params.extraFields) : "";
  return {
    queryKey: [
      "superPageAssignmentsList",
      userId,
      params.superPageId ?? "",
      params.search ?? "",
      params.completed ?? "",
      params.viewed ?? "",
      params.status ?? "",
      params.categoryId ?? "",
      params.uncategorized ?? "",
      extraFieldsKey,
      params.page,
      params.perPage,
      params.sort,
      params.order,
    ] as const,
    queryFn: () => SuperPageServices.getSuperPageAssignments(params, options),
    staleTime: 1000 * 60 * 5, // 5 minutes
  };
}

export const useSuperPageAssignmentSearch = (
  params: GetSuperPageAssignmentsParams = {},
  options?: { enabled?: boolean }
) => {
  const { userId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const { data: superPageAssignments, isFetching } = useQuery({
    ...buildSuperPageAssignmentsQueryOptions(userId, params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });

  const fetchSuperPageAssignments = (fetchParams: GetSuperPageAssignmentsParams = {}, options?: CancelRequestOptions) =>
    queryClient.fetchQuery(buildSuperPageAssignmentsQueryOptions(userId, fetchParams, options));

  const clearSuperPageAssignmentsSearchCache = () => {
    queryClient.invalidateQueries({ queryKey: ["superPageAssignmentsList", userId] }).catch(console.error);
  };

  return {
    superPageAssignments,
    isFetching,
    fetchSuperPageAssignments,
    clearSuperPageAssignmentsSearchCache,
  };
};
